import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Flex, VStack, HStack, Text, Icon, Button, Input, Select,
  Spinner, Alert, AlertIcon, AlertDescription, IconButton, Tooltip,
  SimpleGrid, useToast,
} from '@chakra-ui/react';
import {
  FiLogIn, FiLogOut, FiInfo, FiRefreshCw, FiDownload, FiWifiOff,
  FiChevronLeft, FiChevronRight, FiAlertTriangle, FiMaximize2, FiMapPin, FiExternalLink, FiFileText,
} from 'react-icons/fi';
import { getAccessLogs, listScanners, listStudentStatuses } from 'services/qrService';
import { getCustomersWithOptions } from 'services/woo';
import { getSurveyResponse } from 'services/surveyService';
import { useUserAuth, useRoleModuleAccess, MODULES } from 'utils/site';
import { AVATAR_GRADIENTS, getInitials } from 'utils/avatarStyles';
import { normalizeCourseName, codeToDisplayLabel } from 'utils/courseUtils';
import StudentHistoryModal, { findActiveStatus } from '../AccessControl/components/StudentHistoryModal';
import ModalSurveyResponse from 'components/Modal/ModalSurveyResponse';

// Día calendario en hora LOCAL (no UTC) — new Date().toISOString().slice(0,10)
// da el día UTC, que entre ~20:00 y 23:59 hora Chile ya es el día siguiente.
const todayStr = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// "110:Enseñanza Básica - 3 - A" → "3° Básico A"
const displayGroup = (raw) => raw ? codeToDisplayLabel(normalizeCourseName(raw)) : null;

const openProfile = (id) => {
  if (!id) return;
  window.open(`${window.location.origin}/#/admin/usuario/${id}`, '_blank');
};

const ACCESS_TYPE_CONFIG = {
  entry: { icon: FiLogIn,  chipBg: '#DCF1E6', chipColor: '#1E7A4E', label: 'Entrada'  },
  exit:  { icon: FiLogOut, chipBg: '#FCE7CC', chipColor: '#B5631A', label: 'Salida'   },
  info:  { icon: FiInfo,   chipBg: '#E2ECFB', chipColor: '#2A66B4', label: 'Solo info' },
};

// pedagogical/pickup son los 2 motivos vigentes; el resto queda para
// mostrar bien las salidas anticipadas ya registradas antes de este cambio.
const EXIT_REASON_LABELS = {
  pedagogical:   'Salida pedagógica',
  pickup:        'Retiro',
  sick:          'Enfermedad',
  medical:       'Cita médica',
  parent_pickup: 'Retiro apoderado',
  emergency:     'Emergencia',
  authorized:    'Act. autorizada',
  discipline:    'Disciplina',
  other:         'Otro',
};

const STATUS_AT_SCAN_LABELS = {
  suspended:        'Suspendido',
  medical_leave:    'Licencia médica',
  pedagogical_exit: 'Salida pedagógica',
  sick_exit:        'Salida por enfermedad',
};

const STATUS_AT_SCAN_STYLE = {
  suspended:        { bg: 'var(--chakra-colors-danger-bg, #FEE2E2)', color: 'var(--chakra-colors-danger, #DC2626)' },
  medical_leave:    { bg: 'var(--chakra-colors-warn-bg, #FEF3C7)',  color: 'var(--chakra-colors-warn, #D97706)'  },
  pedagogical_exit: { bg: '#EDE9F8', color: '#6D28D9' },
  sick_exit:        { bg: '#CCFBF1', color: '#0F766E' },
};

const PER_PAGE_OPTIONS = [25, 50, 100];

// Gradiente por RUT (determinístico)
const rutGradient = (rut) => {
  let h = 0;
  for (let i = 0; i < (rut || '').length; i++) h = (h * 31 + rut.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
};

// ── Chip inline ───────────────────────────────────────────────────────────────
const Chip = ({ icon, label, bg, color, style = {} }) => (
  <Flex
    as="span" display="inline-flex" align="center" gap="5px"
    px="10px" py="4px" borderRadius="999px"
    bg={bg} color={color} fontSize="12px" fontWeight="700"
    whiteSpace="nowrap" flexShrink={0} style={style}
  >
    {icon && <Icon as={icon} boxSize="12px" />}
    {label}
  </Flex>
);

// ── Celda de alumno ───────────────────────────────────────────────────────────
const StudentCell = ({ rut, nombre, course, currentStatus, profileId, onClick }) => {
  const name = nombre || rut;
  const initials = getInitials(name);
  const grad = rutGradient(rut);
  const statusStyle = currentStatus ? (STATUS_AT_SCAN_STYLE[currentStatus] ?? {}) : null;
  return (
    <Flex align="center" gap="11px">
      <Box
        w="38px" h="38px" borderRadius="full" flexShrink={0}
        bgGradient={grad} display="flex" alignItems="center" justifyContent="center"
        color="#fff" fontWeight="700" fontSize="12px"
      >
        {initials}
      </Box>
      <Box minW={0}>
        <HStack spacing="6px">
          <Text
            fontWeight="700" fontSize="14px" color="ink.900" noOfLines={1}
            cursor={onClick ? 'pointer' : 'default'}
            _hover={onClick ? { color: 'coral.600', textDecoration: 'underline' } : undefined}
            onClick={onClick}
          >
            {name}
          </Text>
          {currentStatus && (
            <Tooltip label={`Estado vigente ahora: ${STATUS_AT_SCAN_LABELS[currentStatus] ?? currentStatus}`}>
              <Box as="span" display="inline-flex" alignItems="center" flexShrink={0}>
                <Box
                  display={{ base: 'block', md: 'none' }}
                  w="8px" h="8px" borderRadius="full" bg={statusStyle.color}
                />
                <Box
                  display={{ base: 'none', md: 'block' }}
                  px="6px" py="1px" borderRadius="999px" fontSize="10px" fontWeight="700"
                  bg={statusStyle.bg} color={statusStyle.color}
                >
                  {STATUS_AT_SCAN_LABELS[currentStatus] ?? currentStatus}
                </Box>
              </Box>
            </Tooltip>
          )}
          {profileId && (
            <Tooltip label="Ver perfil (nueva pestaña)">
              <IconButton
                icon={<FiExternalLink />} aria-label="Ver perfil"
                size="xs" variant="ghost" color="ink.400" minW="20px" h="20px"
                display={{ base: 'none', md: 'inline-flex' }}
                _hover={{ color: 'coral.500', bg: 'coral.tint' }}
                onClick={(e) => { e.stopPropagation(); openProfile(profileId); }}
              />
            </Tooltip>
          )}
        </HStack>
        <HStack spacing="6px">
          <Text fontSize="11.5px" color="ink.400" fontWeight="500" mt="1px">RUT {rut}</Text>
          {course && (
            <Text fontSize="11px" color="ink.400" fontWeight="600" mt="1px">· {course}</Text>
          )}
        </HStack>
      </Box>
    </Flex>
  );
};

const AccessLogs = () => {
  const { user } = useUserAuth();
  const hasAccessControlAccess = useRoleModuleAccess(MODULES.ACCESS_CONTROL);
  const [logs, setLogs]         = useState([]);
  const [scanners, setScanners] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [meta, setMeta]         = useState({ total: 0, last_page: 1, current_page: 1 });
  const [studentInfoByRut, setStudentInfoByRut] = useState({});
  const [studentStatuses, setStudentStatuses] = useState([]);
  const [historyStudent, setHistoryStudent] = useState(null);
  const [isFichaOpen, setIsFichaOpen] = useState(false);
  const [fichaData, setFichaData] = useState(null);
  const studentCacheRef = useRef({});

  const [filters, setFilters] = useState({
    scanner_id:  '',
    rut:         '',
    date:        '',
    access_type: '',
    per_page:    50,
    page:        1,
  });

  const toast = useToast();

  const fetchLogs = useCallback(async (activeFilters) => {
    setLoading(true);
    setError(null);
    try {
      const params = Object.fromEntries(
        Object.entries(activeFilters).filter(([, v]) => v !== '' && v !== null)
      );
      const res = await getAccessLogs(params);
      const data = res.data ?? [];
      setLogs(data);
      setMeta(res.meta ?? { total: 0, last_page: 1, current_page: 1 });
      enrichStudents([...new Set(data.map(l => l.rut))]);
    } catch {
      setError('No se pudo cargar el historial de accesos.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  // "Ver ficha" directo desde la tabla principal — mismo modal con cabecera/
  // logo/PDF ya usado en Perfil > Fichas, resumen del Scanner y los otros
  // listados de salidas (StudentHistoryModal.js, SickExitsTab.js, etc.).
  const handleOpenFicha = (responseId) => {
    setFichaData(null);
    setIsFichaOpen(true);
    getSurveyResponse(responseId)
      .then(setFichaData)
      .catch(() => setFichaData({ error: 'No se pudieron cargar los datos de la ficha' }));
  };

  // Enriquece RUTs visibles con su curso — mismo servicio /datofy/v1/users que "Por Curso"
  const enrichStudents = useCallback(async (ruts) => {
    const toFetch = ruts.filter(r => r && !studentCacheRef.current[r]);
    if (!toFetch.length) return;
    const BATCH = 5;
    for (let i = 0; i < toFetch.length; i += BATCH) {
      const chunk = toFetch.slice(i, i + BATCH);
      const results = await Promise.all(chunk.map(rut =>
        getCustomersWithOptions({ search: rut, role: 'all', per_page: 5 })
          .then(res => (res.data ?? []).find(c => c.username === rut) ?? null)
          .catch(() => null)
      ));
      const update = {};
      chunk.forEach((rut, idx) => { update[rut] = results[idx] ?? {}; });
      Object.assign(studentCacheRef.current, update);
      setStudentInfoByRut(prev => ({ ...prev, ...update }));
    }
  }, []);

  const fetchScanners = useCallback(async () => {
    try {
      const res = await listScanners();
      setScanners(res.data ?? []);
    } catch { /* opcional */ }
  }, []);

  // Estados vigentes (suspendido/licencia/salida pedagógica) — mismo cruce que usa "Por Curso"
  const fetchStudentStatuses = useCallback(async () => {
    try {
      const res = await listStudentStatuses({ active: true });
      setStudentStatuses(res.data ?? []);
    } catch { /* opcional */ }
  }, []);

  useEffect(() => {
    fetchLogs(filters);
    fetchScanners();
    fetchStudentStatuses();
  }, []); // eslint-disable-line

  const applyFilters = () => {
    const updated = { ...filters, page: 1 };
    setFilters(updated);
    fetchLogs(updated);
  };

  const clearFilters = () => {
    const reset = {
      scanner_id: '', rut: '',
      date: '',
      access_type: '', per_page: 50, page: 1,
    };
    setFilters(reset);
    fetchLogs(reset);
  };

  const goToPage = (page) => {
    const updated = { ...filters, page };
    setFilters(updated);
    fetchLogs(updated);
  };

  const handleExportCSV = () => {
    if (!logs.length) return;
    const headers = ['Fecha y Hora','RUT','Tipo','Lector','Lugar','Modo','Salida Anticipada','Motivo Salida','Apoderado','Notas','Estado al Escanear','Estado Vigente Actual'];
    const rows = logs.map(l => {
      const current = findActiveStatus(studentStatuses, l.rut, todayStr());
      return [
        formatDate(l.scanned_at), l.rut,
        ACCESS_TYPE_CONFIG[l.access_type]?.label ?? l.access_type,
        l.scanner_id ?? '', l.location ?? '',
        l.is_offline ? 'Offline' : 'Online',
        l.early_exit ? 'Sí' : 'No',
        l.exit_reason ? (EXIT_REASON_LABELS[l.exit_reason] ?? l.exit_reason) : '',
        l.guardian_present === null || l.guardian_present === undefined ? '' : (l.guardian_present ? 'Sí' : 'No'),
        l.exit_notes ?? '',
        l.status_at_scan ? (STATUS_AT_SCAN_LABELS[l.status_at_scan] ?? l.status_at_scan) : '',
        current ? (STATUS_AT_SCAN_LABELS[current.type] ?? current.type) : '',
      ];
    });
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `accesos-${filters.date || 'todos'}.csv`;
    link.id = 'access-logs-export-btn-trigger';
    link.click();
    toast({ title: 'CSV descargado', status: 'success', duration: 2000 });
  };

  // Botón oculto para que AccessControl pueda disparar el export desde el topbar.
  // El nodo se crea una sola vez; solo el handler se actualiza cuando cambian
  // logs/filters (evita recrear/reinsertar el DOM en cada tecla del filtro).
  useEffect(() => {
    const btn = document.createElement('button');
    btn.id = 'access-logs-export-btn';
    btn.style.display = 'none';
    document.body.appendChild(btn);
    return () => btn.remove();
  }, []);

  useEffect(() => {
    const btn = document.getElementById('access-logs-export-btn');
    if (btn) btn.onclick = handleExportCSV;
  }, [logs, filters]); // eslint-disable-line

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const fmtTime = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  };

  const statsEntries   = logs.filter(l => l.access_type === 'entry').length;
  const statsExits     = logs.filter(l => l.access_type === 'exit').length;
  const statsEarly     = logs.filter(l => l.early_exit).length;
  const statsInfo      = logs.filter(l => l.access_type === 'info').length;
  const statsOffline   = logs.filter(l => l.is_offline).length;

  if (user && !hasAccessControlAccess) {
    return (
      <Alert status="error" borderRadius="16px">
        <AlertIcon />
        <AlertDescription>No tienes permisos para ver esta sección.</AlertDescription>
      </Alert>
    );
  }

  return (
    <Box fontFamily="jakarta">

      {/* ── Stats tiles ─────────────────────────────────────────────────────── */}
      <SimpleGrid columns={{ base: 2, sm: 3, lg: 5 }} gap="14px" mb={5}>
        {[
          { label: 'Entradas hoy',      value: statsEntries, bg: '#DCF1E6', color: '#1E7A4E', icon: FiLogIn        },
          { label: 'Salidas hoy',       value: statsExits,   bg: '#FCE7CC', color: '#B5631A', icon: FiLogOut       },
          { label: 'Salidas anticipadas',value: statsEarly,  bg: '#FEF3C7', color: '#92400E', icon: FiAlertTriangle},
          { label: 'Solo consultas',    value: statsInfo,    bg: '#E2ECFB', color: '#2A66B4', icon: FiInfo         },
          { label: 'Capturados offline', value: statsOffline, bg: '#FEF3C7', color: '#D48F0B', icon: FiWifiOff      },
        ].map(({ label, value, bg, color, icon }) => (
          <Flex
            key={label}
            bg="white" border="1px solid" borderColor="cream.line"
            borderRadius="16px" p={{ base: 3, md: 4 }} gap={{ base: '10px', md: '14px' }} align="center"
            boxShadow="0 2px 8px -2px rgba(35,30,26,.06)"
          >
            <Flex
              w={{ base: '34px', md: '42px' }} h={{ base: '34px', md: '42px' }} borderRadius="12px" bg={bg}
              align="center" justify="center" flexShrink={0}
            >
              <Icon as={icon} color={color} boxSize={{ base: '16px', md: '21px' }} />
            </Flex>
            <Box minW={0}>
              <Text fontSize={{ base: '19px', md: '26px' }} fontWeight="800" color="ink.900" lineHeight={1} letterSpacing="-0.03em" noOfLines={1}>
                {value}
              </Text>
              <Text fontSize={{ base: '11px', md: '12px' }} color="ink.400" fontWeight="600" mt="4px" noOfLines={1}>{label}</Text>
            </Box>
          </Flex>
        ))}
      </SimpleGrid>

      {/* ── Filter bar ──────────────────────────────────────────────────────── */}
      <Flex align="center" gap="10px" mb={4} flexWrap="wrap">
        {/* Search RUT */}
        <Box position="relative" w={{ base: 'full', sm: '240px' }} flexShrink={0}>
          <Icon
            as={FiLogIn} position="absolute" left="13px" top="50%"
            transform="translateY(-50%)" boxSize="17px" color="ink.400"
          />
          <Input
            placeholder="RUT o nombre del alumno…"
            value={filters.rut}
            onChange={e => setFilters(f => ({ ...f, rut: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && applyFilters()}
            h="40px" pl="39px" borderRadius="11px"
            border="1.5px solid" borderColor="cream.line2"
            bg="white" fontSize="14px" color="ink.900"
            _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
          />
        </Box>

        <Input
          type="date"
          value={filters.date}
          onChange={e => setFilters(f => ({ ...f, date: e.target.value }))}
          h="40px" borderRadius="11px"
          border="1.5px solid" borderColor="cream.line2"
          bg="white" fontSize="13.5px" fontWeight="600" color="ink.700"
          w={{ base: 'full', sm: '160px' }}
          _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
        />

        <Select
          placeholder="Todo tipo"
          value={filters.access_type}
          onChange={e => setFilters(f => ({ ...f, access_type: e.target.value }))}
          h="40px" borderRadius="11px"
          border="1.5px solid" borderColor="cream.line2"
          bg="white" fontSize="13.5px" fontWeight="600" color="ink.700"
          w={{ base: 'full', sm: '130px' }}
          _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
        >
          <option value="entry">Entrada</option>
          <option value="exit">Salida</option>
          <option value="info">Solo info</option>
        </Select>

        <Select
          placeholder="Todos los lectores"
          value={filters.scanner_id}
          onChange={e => setFilters(f => ({ ...f, scanner_id: e.target.value }))}
          h="40px" borderRadius="11px"
          border="1.5px solid" borderColor="cream.line2"
          bg="white" fontSize="13.5px" fontWeight="600" color="ink.700"
          w={{ base: 'full', sm: '180px' }}
          _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
        >
          {scanners.map(s => <option key={s.scanner_id} value={s.scanner_id}>{s.name}</option>)}
        </Select>

        <Select
          value={filters.per_page}
          onChange={e => setFilters(f => ({ ...f, per_page: parseInt(e.target.value) }))}
          h="40px" borderRadius="11px"
          border="1.5px solid" borderColor="cream.line2"
          bg="white" fontSize="13.5px" fontWeight="600" color="ink.700"
          w={{ base: 'full', sm: '110px' }}
          _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
        >
          {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n} / pág.</option>)}
        </Select>

        <Button
          h="40px" px={4} borderRadius="11px"
          bg="coral.500" color="#fff" fontWeight="700" fontSize="13.5px"
          _hover={{ bg: 'coral.600' }}
          onClick={applyFilters}
        >
          Filtrar
        </Button>
        <Button
          h="40px" px="13px" borderRadius="11px"
          border="1.5px solid" borderColor="cream.line2"
          bg="white" fontWeight="700" fontSize="13.5px" color="ink.400"
          _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
          onClick={clearFilters}
        >
          Limpiar
        </Button>

        <Box ml="auto" display={{ base: 'none', md: 'block' }}>
          <Button
            h="40px" px={4} borderRadius="11px"
            border="1.5px solid" borderColor="cream.line2"
            bg="white" color="ink.700" fontWeight="700" fontSize="13.5px"
            _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
            leftIcon={<FiDownload size={14} />}
            onClick={handleExportCSV}
            isDisabled={!logs.length}
          >
            Exportar CSV
          </Button>
        </Box>
      </Flex>

      {/* ── Tabla ─────────────────────────────────────────────────────────────── */}
      <Box
        bg="white" border="1px solid" borderColor="cream.line"
        borderRadius="22px" overflow="hidden"
        boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
      >
        {error && (
          <Alert status="error" m={4} borderRadius="10px">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <VStack py={14} spacing={3}>
            <Spinner size="lg" color="coral.500" />
            <Text fontSize="13px" color="ink.400">Cargando registros…</Text>
          </VStack>
        ) : logs.length === 0 ? (
          <VStack py={14} spacing={2}>
            <Icon as={FiAlertTriangle} color="ink.400" boxSize={8} />
            <Text color="ink.400" fontSize="13px">Sin registros para los filtros aplicados</Text>
          </VStack>
        ) : (
          <>
            {/* Encabezado */}
            <Flex
              bg="cream.bg2" px={4} py="12px"
              borderBottom="1px solid" borderColor="cream.line"
            >
              {/* "Conexión" (Online/Offline por fila) se sacó de acá — is_offline
                  es una marca permanente de captura, no un estado que requiera
                  atención, y mostrarla fila por fila con el mismo tono de chip
                  que las alertas reales generaba confusión. El resumen del día
                  ya queda cubierto por el banner y la tarjeta "Capturados
                  offline" de arriba; el detalle exacto sigue disponible en la
                  exportación CSV. */}
              {['Alumno', 'Tipo', 'Lector', 'Lugar', 'Detalle / Estado especial', 'Fecha y hora'].map((h, i) => (
                <Text
                  key={h} fontSize="11.5px" fontWeight="800" color="ink.400"
                  textTransform="uppercase" letterSpacing=".05em" whiteSpace="nowrap"
                  flex={i === 0 ? 2 : i === 4 ? 1.5 : 1} minW={0}
                  display={i >= 2 && i <= 4 ? { base: 'none', md: 'block' } : i === 1 || i === 5 ? 'block' : { base: 'none', lg: 'block' }}
                >
                  {h}
                </Text>
              ))}
            </Flex>

            {/* Filas */}
            {logs.map(log => {
              const cfg = ACCESS_TYPE_CONFIG[log.access_type] ?? ACCESS_TYPE_CONFIG.entry;
              const scanStyle = log.status_at_scan ? (STATUS_AT_SCAN_STYLE[log.status_at_scan] ?? {}) : {};
              const info = studentInfoByRut[log.rut];
              const fullName = [info?.first_name, info?.last_name].filter(Boolean).join(' ').trim()
                || log.nombre || null;
              const profileId = info?.id ?? log.wp_user_id ?? null;
              return (
                <Flex
                  key={log.id}
                  px={4} py="13px"
                  borderTop="1px solid" borderColor="cream.line"
                  align="center" gap="12px"
                  _hover={{ bg: 'coral.tint' }} transition="background .13s"
                >
                  {/* Alumno */}
                  <Box flex={2} minW={0}>
                    <StudentCell
                      rut={log.rut} nombre={fullName}
                      course={displayGroup(info?.meta_data?.find(m => m.key === 'group_name')?.value)}
                      currentStatus={findActiveStatus(studentStatuses, log.rut, todayStr())?.type ?? null}
                      profileId={profileId}
                      onClick={() => setHistoryStudent({
                        username: log.rut,
                        id: profileId,
                        first_name: info?.first_name ?? log.nombre ?? log.rut,
                        last_name: info?.last_name ?? '',
                      })}
                    />
                  </Box>

                  {/* Tipo */}
                  <Box flex={1} minW={0}>
                    <Chip icon={cfg.icon} label={cfg.label} bg={cfg.chipBg} color={cfg.chipColor} />
                  </Box>

                  {/* Lector */}
                  <Box flex={1} minW={0} display={{ base: 'none', md: 'block' }}>
                    <Flex align="center" gap="4px">
                      <Icon as={FiMapPin} boxSize="13px" color="ink.400" />
                      <Text fontSize="13px" fontWeight="600" color="ink.700" noOfLines={1}>
                        {log.scanner_id ?? '—'}
                      </Text>
                    </Flex>
                  </Box>

                  {/* Lugar */}
                  <Box flex={1} minW={0} display={{ base: 'none', lg: 'block' }}>
                    <Text fontSize="13px" color="ink.400" fontWeight="600" noOfLines={1}>
                      {log.location ?? '—'}
                    </Text>
                  </Box>

                  {/* Detalle */}
                  <Box flex={1.5} minW={0} display={{ base: 'none', md: 'block' }}>
                    <Flex gap="5px" flexWrap="wrap">
                      {log.early_exit && (
                        <Chip
                          label={`Salida anticipada${log.exit_reason ? ` — ${EXIT_REASON_LABELS[log.exit_reason] ?? log.exit_reason}` : ''}`}
                          bg="#FEF3C7" color="#92400E"
                        />
                      )}
                      {log.status_at_scan && (
                        <Chip
                          label={STATUS_AT_SCAN_LABELS[log.status_at_scan] ?? log.status_at_scan}
                          bg={scanStyle.bg} color={scanStyle.color}
                        />
                      )}
                      {!log.early_exit && !log.status_at_scan && (
                        <Text fontSize="13px" color="ink.400">—</Text>
                      )}
                    </Flex>
                  </Box>

                  {/* Ficha — visible directo en la fila, sin tener que abrir
                      el historial del alumno (StudentHistoryModal ya la
                      tenía, esto la agrega también acá). */}
                  {log.survey_response_id && (
                    <Tooltip label="Ver ficha">
                      <IconButton
                        icon={<FiFileText />} w="30px" h="30px" minW="30px" borderRadius="9px"
                        bg="butter.bg" color="butter.ink" border="none" flexShrink={0}
                        _hover={{ bg: '#FCE7CC' }}
                        onClick={() => handleOpenFicha(log.survey_response_id)}
                        aria-label="Ver ficha"
                      />
                    </Tooltip>
                  )}

                  {/* Hora */}
                  <Box textAlign="right" flexShrink={0}>
                    <Text fontSize="13.5px" fontWeight="700" color="ink.900" whiteSpace="nowrap">
                      {fmtTime(log.scanned_at)} hrs
                    </Text>
                    <Text fontSize="11.5px" color="ink.400" fontWeight="500" mt="2px">
                      {new Date(log.scanned_at).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })}
                    </Text>
                  </Box>
                </Flex>
              );
            })}
          </>
        )}

        {/* ── Paginación ─────────────────────────────────────────────────── */}
        {!loading && meta.last_page > 1 && (
          <Flex
            justify="space-between" align="center"
            px={{ base: 3, md: 4 }} py={3}
            borderTop="1px solid" borderColor="cream.line"
            flexWrap="wrap" gap={2}
          >
            <Text fontSize="13px" color="ink.400" fontWeight="600">
              Mostrando {logs.length} de {meta.total.toLocaleString('es-CL')} registros
            </Text>
            <HStack spacing="6px">
              <IconButton
                icon={<FiChevronLeft />} size="sm"
                borderRadius="10px" border="1.5px solid" borderColor="cream.line2"
                bg="white" color="ink.700" _hover={{ borderColor: 'coral.500' }}
                isDisabled={meta.current_page <= 1}
                onClick={() => goToPage(meta.current_page - 1)}
                aria-label="Anterior"
              />
              {Array.from({ length: Math.min(5, meta.last_page) }, (_, i) => {
                const p = Math.max(1, Math.min(meta.current_page - 2, meta.last_page - 4)) + i;
                const isActive = p === meta.current_page;
                return (
                  <Button
                    key={p} size="sm" minW="36px"
                    borderRadius="10px"
                    border="1.5px solid"
                    borderColor={isActive ? 'coral.500' : 'cream.line2'}
                    bg={isActive ? 'coral.500' : 'white'}
                    color={isActive ? '#fff' : 'ink.700'}
                    fontWeight="700"
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </Button>
                );
              })}
              <IconButton
                icon={<FiChevronRight />} size="sm"
                borderRadius="10px" border="1.5px solid" borderColor="cream.line2"
                bg="white" color="ink.700" _hover={{ borderColor: 'coral.500' }}
                isDisabled={meta.current_page >= meta.last_page}
                onClick={() => goToPage(meta.current_page + 1)}
                aria-label="Siguiente"
              />
            </HStack>
          </Flex>
        )}
      </Box>

      <StudentHistoryModal
        isOpen={!!historyStudent}
        student={historyStudent}
        onClose={() => setHistoryStudent(null)}
      />

      <ModalSurveyResponse
        isOpen={isFichaOpen}
        onClose={() => setIsFichaOpen(false)}
        customer={{ first_name: fichaData?.survey?.name ?? '', last_name: '', username: '' }}
        data={fichaData}
      />
    </Box>
  );
};

export default AccessLogs;
